import React, { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, doc, updateDoc, serverTimestamp, addDoc, getDoc } from "firebase/firestore";
import HoldModal from "./holdModal";

const BackInventoryView = ({ user }) => {
    const [inventory, setInventory] = useState([]);
    const [showOutOfStock, setShowOutOfStock] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            const snapshot = await getDocs(collection(db, "inventory"));
            const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setInventory(items);
        };

        fetchData();
    }, []);

    const handleUpdateBackdoor = async (item, newValue) => {
        try {
            const userDoc = await getDoc(doc(db, "users", user.uid));
            const fullName = `${userDoc.data()?.firstName || ""} ${userDoc.data()?.lastName || ""}`.trim();

            // Fetch the latest backdoor value directly from Firestore to avoid stale data
            const itemRef = doc(db, "inventory", item.id);
            const currentItem = await getDoc(itemRef);
            const currentBackdoor = currentItem.data()?.backdoor || 0;
            const change = newValue - currentBackdoor;

            // Update Firestore
            await updateDoc(itemRef, {
                backdoor: newValue,
                lastUpdated: serverTimestamp(),
            });

            // Log the update action
            await addDoc(collection(db, "logs"), {
                user: fullName || user.email,
                action: "Backdoor Updated",
                brand: item.name || "Unknown",
                section: "backdoor",
                field: "backdoor",
                old: currentBackdoor,
                new: newValue,
                change: change,
                timestamp: serverTimestamp(),
            });

            // Update local state
            setInventory((prev) =>
                prev.map((i) =>
                    i.id === item.id
                        ? {
                              ...i,
                              backdoor: newValue,
                          }
                        : i
                )
            );
        } catch (error) {
            console.error("❌ Error updating backdoor:", error);
        }
    };

    const handleMarkAsOnHold = (item, setSelectedItem, setShowModal) => {
        if ((item.visible ?? 0) <= 0 && (item.backdoor ?? 0) <= 0) return;

        setSelectedItem(item);
        setShowModal(true);
    };

    return (
        <div className="p-4 max-w-3xl mx-auto">
            <h2 className="text-xl font-bold mb-4">Back Inventory</h2>
            <table className="w-full border">
                <thead className="bg-gray-200 text-left">
                    <tr>
                        <th className="p-2">Brand</th>
                        <th className="p-2">Backdoor</th>
                        <th className="p-2 text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {inventory
                        .filter(item => item.backdoor > 0 || showOutOfStock)
                        .map((item) => (
                            <tr key={item.id} className="border-t even:bg-gray-50">
                                <td className="p-2">{item.name}</td>
                                <td className="p-2">
                                    <input
                                        type="number"
                                        className="border p-1 w-20 text-center"
                                        value={item.backdoor ?? 0}
                                        onChange={(e) => {
                                            const newValue = parseInt(e.target.value);
                                            if (!isNaN(newValue) && newValue >= 0) {
                                                handleUpdateBackdoor(item, newValue);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") e.target.blur();
                                        }}
                                    />
                                </td>
                                <td className="p-2 text-center">
                                    <button
                                        disabled={(item.backdoor ?? 0) <= 0}
                                        onClick={() => handleMarkAsOnHold(item, setSelectedItem, setShowModal)}
                                        className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-xs rounded"
                                    >
                                        Put on Hold
                                    </button>
                                </td>
                            </tr>
                        ))}
                </tbody>
            </table>

            {inventory.some(item => (item.backdoor ?? 0) === 0) && !showOutOfStock && (
                <div className="mt-4 text-center">
                    <button
                        className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
                        onClick={() => setShowOutOfStock(true)}
                    >
                        Show Out of Stock
                    </button>
                </div>
            )}

            {showModal && selectedItem && (
                <HoldModal
                    item={selectedItem}
                    user={user}
                    setShowModal={setShowModal}
                    setInventory={setInventory}
                />
            )}
        </div>
    );
};

export default BackInventoryView;
